import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Document, Page, pdfjs } from "react-pdf";
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// PDF.js worker 설정 (로컬 worker 사용하여 안정성 향상)
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// PDF 옵션 설정
const pdfOptions = {
  cMapUrl: 'https://unpkg.com/pdfjs-dist@latest/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@latest/standard_fonts/',
};

// PDF 파일들 import (안전하게 처리)
let document1, document2, document3, document4, document4_2, document5, document6, document7, document8;

try {
  document1 = require("../../Assets/document_1.pdf");
} catch (e) {
  console.warn("document_1.pdf not found");
}

try {
  document2 = require("../../Assets/document_2.pdf");
} catch (e) {
  console.warn("document_2.pdf not found");
}

try {
  document3 = require("../../Assets/document_3.pdf");
} catch (e) {
  console.warn("document_3.pdf not found");
}

try {
  document4 = require("../../Assets/document_4.pdf");
} catch (e) {
  console.warn("document_4.pdf not found");
}

try {
  document4_2 = require("../../Assets/document_4_2.pdf");
} catch (e) {
  console.warn("document_4_2.pdf not found");
}

try {
  document5 = require("../../Assets/document_5.pdf");
} catch (e) {
  console.warn("document_5.pdf not found");
}

try {
  document6 = require("../../Assets/document_6.pdf");
} catch (e) {
  console.warn("document_6.pdf not found");
}

try {
  document7 = require("../../Assets/document_7.pdf");
} catch (e) {
  console.warn("document_7.pdf not found");
}

try {
  document8 = require("../../Assets/document_8.pdf");
} catch (e) {
  console.warn("document_8.pdf not found");
}

function Certificates() {
  const [containerWidth, setContainerWidth] = useState(800);
  const [documentErrors, setDocumentErrors] = useState({});
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const [loadedDocuments, setLoadedDocuments] = useState(new Set());
  const containerRef = useRef(null);
  const documentRefs = useRef({});

  // 컴포넌트 마운트 상태 관리
  useEffect(() => {
    setIsComponentMounted(true);
    return () => {
      setIsComponentMounted(false);
      // 모든 PDF 문서 정리
      Object.values(documentRefs.current).forEach(docRef => {
        if (docRef && typeof docRef.destroy === 'function') {
          try {
            docRef.destroy();
          } catch (e) {
            console.warn('PDF cleanup error:', e);
          }
        }
      });
    };
  }, []);

  // PDF 파일 배열 (파일이 존재하는 것만 포함)
  const documents = [
    document1 && { file: document1, title: "사업자등록증", id: "doc1" },
    document2 && { file: document2, title: "건설업등록증", id: "doc2" },
    document3 && { file: document3, title: "귀뚜라미범양냉방인허증", id: "doc3" },
    document4 && { file: document4, title: "냉매회수업1", id: "doc4" },
    document4_2 && { file: document4_2, title: "냉매회수업2", id: "doc4_2" },
    document5 && { file: document5, title: "ISO인증서9001한글", id: "doc5" },
    document6 && { file: document6, title: "ISO인증서9001영어", id: "doc6" },
    document7 && { file: document7, title: "ISO인증서45001한글", id: "doc7" },
    document8 && { file: document8, title: "ISO인증서45001영어", id: "doc8" },
  ].filter(Boolean);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const containerElement = containerRef.current;
        const availableWidth = containerElement.offsetWidth;
        // 최대 너비 800px, 최소 300px로 제한
        const newWidth = Math.min(800, Math.max(300, availableWidth - 40));
        setContainerWidth(newWidth);
      }
    };

    // 초기 너비 설정
    updateWidth();

    // 창 크기 변경 시 너비 업데이트
    const handleResize = () => {
      updateWidth();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // PDF 스케일 계산 (containerWidth 기준)
  const getScale = () => {
    if (containerWidth <= 400) return 0.8;
    if (containerWidth <= 600) return 0.9;
    if (containerWidth <= 800) return 0.9;
    return 1.0;
  };

  // 문서 로드 에러 핸들러 (안전한 방식)
  const handleDocumentLoadError = (docId, error) => {
    if (isComponentMounted) {
      console.error(`Document ${docId} load error:`, error);
      setDocumentErrors(prev => ({ ...prev, [docId]: true }));
      setLoadedDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(docId);
        return newSet;
      });
    }
  };

  // 문서 로드 성공 핸들러 (안전한 방식)
  const handleDocumentLoadSuccess = (docId, pdfDocument) => {
    if (isComponentMounted) {
      setDocumentErrors(prev => ({ ...prev, [docId]: false }));
      setLoadedDocuments(prev => new Set(prev).add(docId));
      documentRefs.current[docId] = pdfDocument;
    }
  };

  // 페이지 로드 에러 핸들러
  const handlePageLoadError = (docId, error) => {
    if (isComponentMounted) {
      console.warn(`Page load error for ${docId}:`, error);
      // 페이지 로드 에러는 무시하고 계속 진행
    }
  };

  return (
    <Container fluid className="certificates-section">
      <Container ref={containerRef}>
        <div className="certificates-header">
          <h1 className="certificates-heading">인증 현황</h1>
          <p className="certificates-description">
            동진에어텍의 각종 인증서 및 자격증을 확인하실 수 있습니다.
          </p>
        </div>

        <div className="certificates-content">
          {documents.length === 0 ? (
            <div className="no-documents">
              <h3>인증서 파일을 찾을 수 없습니다</h3>
              <p>src/Assets/ 폴더에 다음 파일들을 추가해주세요:</p>
              <ul>
                <li>document_1.pdf</li>
                <li>document_2.pdf</li>
                <li>document_3.pdf</li>
                <li>document_4.pdf</li>
                <li>document_4_2.pdf</li>
                <li>document_5.pdf</li>
                <li>document_6.pdf</li>
                <li>document_7.pdf</li>
                <li>document_8.pdf</li>
              </ul>
            </div>
          ) : (
            documents.map((doc, index) => (
              <Row key={index} className="certificate-item">
                <Col>
                  <div className="certificate-wrapper">
                    {isComponentMounted && (
                      <Document
                        file={doc.file}
                        className="certificate-document"
                        onLoadSuccess={(pdfDocument) => handleDocumentLoadSuccess(doc.id, pdfDocument)}
                        onLoadError={(error) => handleDocumentLoadError(doc.id, error)}
                        options={pdfOptions}
                        loading={
                          <div className="certificate-loading">
                            {doc.title} 로딩 중...
                          </div>
                        }
                        error={
                          <div className="certificate-error">
                            {doc.title} 로드 실패<br />
                            <small>PDF 파일을 확인해주세요</small>
                          </div>
                        }
                        onSourceError={(error) => {
                          console.warn(`Source error for ${doc.id}:`, error);
                        }}
                        onPassword={() => {
                          console.warn(`Password required for ${doc.id}`);
                        }}
                      >
                        {loadedDocuments.has(doc.id) && (
                          <Page
                            pageNumber={1}
                            scale={getScale()}
                            width={containerWidth}
                            className="certificate-page"
                            onLoadError={(error) => handlePageLoadError(doc.id, error)}
                            onLoadSuccess={() => {
                              if (isComponentMounted) {
                                console.log(`Page loaded successfully for ${doc.id}`);
                              }
                            }}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            loading={
                              <div className="certificate-loading">
                                페이지 로딩 중...
                              </div>
                            }
                            error={
                              <div className="certificate-error">
                                페이지 로드 실패
                              </div>
                            }
                          />
                        )}
                      </Document>
                    )}
                  </div>
                </Col>
              </Row>
            ))
          )}
        </div>
      </Container>
    </Container>
  );
}

export default Certificates;